<template>
  <div class="document-scanner">
    <!-- Alert Messages - Moved to top for better visibility -->
    <b-alert
      v-if="alertMessage && showAlerts"
      :variant="alertVariant"
      :show="alertMessage !== null"
      dismissible
      @dismissed="alertMessage = null"
      class="fade-in mb-2"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          :icon="getAlertIcon"
          size="18"
          class="mr-1"
        />
        <span>{{ alertMessage }}</span>
      </div>
    </b-alert>

    <!-- Connection Status Badge -->
    <b-badge
      v-if="showStatus"
      :variant="connected ? 'success' : 'danger'"
      class="connection-badge mb-2"
    >
      <feather-icon
        :icon="connected ? 'WifiIcon' : 'WifiOffIcon'"
        size="14"
        class="mr-50"
      />
      {{ connected ? $t("scanner.bridge-connected") : $t("scanner.bridge-offline") }}
    </b-badge>

    <!-- Scan Button -->
    <b-button
      v-if="showScanButton"
      :variant="connected ? 'primary' : 'secondary'"
      :disabled="!connected || scanning"
      @click="handleScan"
      class="scan-button"
    >
      <feather-icon
        :icon="scanning ? 'RefreshCwIcon' : 'CameraIcon'"
        :class="{ 'spin-animation': scanning }"
        size="16"
        class="mr-50"
      />
      {{ scanning ? $t("scanner.scanning") : $t("scanner.scan-document") }}
    </b-button>

    <!-- Scanned Documents Preview (if inline mode) -->
    <div v-if="inline && scannedDocs.length > 0" class="scanned-docs-preview mt-2">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h6 class="mb-0">
          {{ $t("scanner.scanned-documents") }} ({{ scannedDocs.length }})
        </h6>
        <b-button
          variant="outline-danger"
          size="sm"
          @click="clearAllDocuments"
        >
          <feather-icon icon="TrashIcon" size="14" class="mr-50" />
          {{ $t("common.clear-all") }}
        </b-button>
      </div>

      <div class="documents-grid">
        <div
          v-for="doc in scannedDocs"
          :key="doc.id"
          class="document-card"
        >
          <b-button
            variant="danger"
            size="sm"
            class="remove-btn"
            pill
            @click="removeDocument(doc.id)"
          >
            <feather-icon icon="XIcon" size="14" />
          </b-button>
          <b-img
            :src="doc.data"
            :alt="doc.filename"
            fluid
            class="document-thumbnail"
            @click="previewDocument(doc)"
          />
          <div class="document-info">
            <small class="text-muted d-block text-truncate" :title="doc.filename">
              {{ doc.filename }}
            </small>
            <small class="text-muted">{{ formatDate(doc.timestamp) }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <b-modal
      v-model="showPreviewModal"
      :title="previewDoc ? previewDoc.filename : ''"
      size="xl"
      hide-footer
      centered
    >
      <b-img
        v-if="previewDoc"
        :src="previewDoc.data"
        fluid
        class="w-100"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BBadge,
  BAlert,
  BImg,
  BModal,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "DocumentScanner",
  components: {
    BButton,
    BBadge,
    BAlert,
    BImg,
    BModal,
  },
  props: {
    // Show connection status badge
    showStatus: {
      type: Boolean,
      default: true,
    },
    // Show scan button
    showScanButton: {
      type: Boolean,
      default: true,
    },
    // Show alerts inline (if false, will emit them)
    showAlerts: {
      type: Boolean,
      default: true,
    },
    // Inline mode shows preview, otherwise only emits events
    inline: {
      type: Boolean,
      default: false,
    },
    // WebSocket URL
    wsUrl: {
      type: String,
      default: "ws://localhost:8765",
    },
    // Auto-connect on mount
    autoConnect: {
      type: Boolean,
      default: true,
    },
    // DPI for scanning
    dpi: {
      type: Number,
      default: 200,
    },
    // Color mode
    colorMode: {
      type: String,
      default: "RGB",
    },
  },
  data() {
    return {
      ws: null,
      connected: false,
      scanning: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 10,
      reconnectDelay: 5000,
      pingInterval: null,
      scannedDocs: [],
      alertMessage: null,
      alertVariant: "info",
      showPreviewModal: false,
      previewDoc: null,
    };
  },
  computed: {
    getAlertIcon() {
      const icons = {
        success: "CheckCircleIcon",
        danger: "XCircleIcon",
        warning: "AlertTriangleIcon",
        info: "InfoIcon",
      };
      return icons[this.alertVariant] || "InfoIcon";
    },
  },
  mounted() {
    if (this.autoConnect) {
      this.connectToBridge();
    }
  },
  beforeDestroy() {
    this.disconnect();
  },
  methods: {
    connectToBridge() {
      if (
        this.ws &&
        (this.ws.readyState === WebSocket.CONNECTING ||
          this.ws.readyState === WebSocket.OPEN)
      ) {
        console.log("Already connected or connecting");
        return;
      }

      // Show error immediately so download button appears
      this.showAlert(this.$t('scanner.connection-failed'), 'danger')
      this.$emit('connection-status', false)

      try {
        this.ws = new WebSocket(this.wsUrl)

        this.ws.onopen = () => {
          console.log('Connected to scanner bridge')
          this.reconnectAttempts = 0
          this.connected = true
          this.showAlert(this.$t('scanner.connected-success'), 'success')
          this.$emit('connection-status', true)
          this.startPingInterval()
        }

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            console.log('Received:', data.type)
            this.handleServerMessage(data)
          } catch (e) {
            console.error('Failed to parse message:', e)
          }
        }

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          this.connected = false
          this.$emit('connection-status', false)
        }

        this.ws.onclose = () => {
          console.log('Disconnected from scanner bridge')
          this.connected = false
          this.$emit('connection-status', false)
          this.stopPingInterval()

          this.ws = null

          // Auto-reconnect every 5 seconds
          if (this.reconnectAttempts < 999) {
            // Keep trying
            this.reconnectAttempts += 1
            console.log(
              `Reconnection attempt ${this.reconnectAttempts} in 5 seconds...`,
            )
            setTimeout(() => {
              if (!this.connected) {
                this.connectToBridge()
              }
            }, 5000)
          }
        }
      } catch (error) {
        console.error("Failed to connect:", error);
        this.connected = false;
        this.$emit("connection-status", false);
        this.showAlert(this.$t("scanner.bridge-not-detected"), "warning");
      }
    },

    disconnect() {
      this.stopPingInterval();
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
      this.connected = false;
    },

    handleServerMessage(data) {
      switch (data.type) {
        case "connected":
          this.connected = data.scanner_available;
          this.$emit("scanner-info", {
            available: data.scanner_available,
            name: data.scanner_name,
          });
          if (!data.scanner_available) {
            this.showAlert(this.$t("scanner.no-scanner-detected"), "warning");
          }
          break;

        case "scanning":
          this.showAlert(data.message, "info");
          break;

        case "scan_complete":
          this.addScannedDocument(data.image, data.timestamp, "Scanned Document");
          this.scanning = false;
          this.showAlert(this.$t("scanner.scan-success"), "success");
          break;

        case "error":
          this.showAlert(data.message, "danger");
          this.scanning = false;
          break;

        case "scanners_list":
          console.log("Available scanners:", data.scanners);
          this.$emit("scanners-list", data.scanners);
          break;

        case "pong":
          console.log("Pong received");
          break;

        default:
          console.warn("Unknown message type:", data.type);
      }
    },

    startPingInterval() {
      this.stopPingInterval();
      this.pingInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    },

    stopPingInterval() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    },

    handleScan() {
      if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.showAlert(this.$t("scanner.bridge-not-running"), "danger");
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("scanner.error"),
            icon: "XCircleIcon",
            variant: "danger",
            text: this.$t("scanner.bridge-not-running"),
          },
        });
        return;
      }

      if (this.scanning) {
        this.showAlert(this.$t("scanner.scan-in-progress"), "warning");
        return;
      }

      this.scanning = true;
      this.showAlert(this.$t("scanner.sending-request"), "info");

      try {
        this.ws.send(
          JSON.stringify({
            type: "scan",
            dpi: this.dpi,
            color_mode: this.colorMode,
          })
        );
      } catch (e) {
        console.error("Failed to send scan request:", e);
        this.showAlert(this.$t("scanner.communication-failed"), "danger");
        this.scanning = false;
      }
    },

    addScannedDocument(imageData, timestamp, filename = "Scanned Document") {
      const doc = {
        id: Date.now() + Math.random(),
        data: imageData,
        timestamp: timestamp,
        filename: filename,
      };

      this.scannedDocs.push(doc);
      this.$emit("document-scanned", doc);
      this.$emit("documents-updated", this.scannedDocs);
    },

    removeDocument(docId) {
      this.scannedDocs = this.scannedDocs.filter((doc) => doc.id !== docId);
      this.$emit("documents-updated", this.scannedDocs);
      this.showAlert(this.$t("scanner.document-removed"), "info");
    },

    clearAllDocuments() {
      if (this.scannedDocs.length === 0) return;

      this.$bvModal
        .msgBoxConfirm(
          this.$t("scanner.confirm-clear-all", { count: this.scannedDocs.length }),
          {
            title: this.$t("common.confirm"),
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.$t("common.yes"),
            cancelTitle: this.$t("common.cancel"),
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.scannedDocs = [];
            this.$emit("documents-updated", this.scannedDocs);
            this.showAlert(this.$t("scanner.all-cleared"), "info");
          }
        });
    },

    previewDocument(doc) {
      this.previewDoc = doc;
      this.showPreviewModal = true;
    },

    showAlert(message, variant = "info") {
      // If showAlerts is false, emit the alert to parent component
      if (!this.showAlerts) {
        this.$emit('alert-message', { message, variant });
        return;
      }

      // Otherwise, show alert internally
      this.alertMessage = message;
      this.alertVariant = variant;

      // Auto-hide success messages
      if (variant === "success") {
        setTimeout(() => {
          this.alertMessage = null;
        }, 5000);
      }
    },

    formatDate(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString();
    },

    // Public methods
    getScannedDocuments() {
      return this.scannedDocs;
    },

    clearDocuments() {
      this.scannedDocs = [];
      this.$emit("documents-updated", this.scannedDocs);
    },

    reconnect() {
      this.reconnectAttempts = 0;
      this.connectToBridge();
    },
  },
};
</script>

<style lang="scss" scoped>
.document-scanner {
  .connection-badge {
    display: inline-flex;
    align-items: center;
  }

  .fade-in {
    animation: fadeIn 0.3s ease-in;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .scan-button {
    .spin-animation {
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .scanned-docs-preview {
    .documents-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 1rem;
      margin-top: 1rem;

      .document-card {
        position: relative;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 0.5rem;
        background: #f8f9fa;
        transition: all 0.3s ease;

        &:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);

          .remove-btn {
            opacity: 1;
          }
        }

        .remove-btn {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          z-index: 10;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .document-thumbnail {
          width: 100%;
          height: 120px;
          object-fit: cover;
          border-radius: 4px;
          cursor: pointer;
          margin-bottom: 0.5rem;
        }

        .document-info {
          font-size: 0.75rem;
        }
      }
    }
  }
}
</style>
